import {
    CreateBeddingRequestInput,
    CreateEventClassEntryInput,
    CreateEventEntryFeeInput,
    CreateEventEntryInput,
    CreateEventRulesAgreementInput,
    CreateStablingRequestInput,
} from "../../API";
import { Result } from "../../interfaces/Result";
import {
    BeddingRequest,
    Event,
    EventBeddingType,
    EventClass,
    EventClassEntry,
    EventDivision,
    EventEntry,
    EventEntryFee,
    EventFee,
    EventRules,
    EventRulesAgreement,
    EventStallType,
    StablingRequest,
} from "../../models";
import { getBeddingRequestsByEntryId, createBeddingRequest } from "../beddingRequest/BeddingRequest";
import { getEventBeddingTypesByEventId } from "../eventBeddingType/EventBeddingType";
import { getEventClassesByEventId } from "../eventClass/EventClass";
import { createEventClassEntry, getEventClassEntriesWithEventClassByEventEntryId } from "../eventClassEntry/EventClassEntry";
import { getEventDivisionById, getEventDivisionsByEventId } from "../eventDivision/EventDivision";
import { createEventEntryFee, getEventEntryFeesByEventIdByEntryId } from "../eventEntryFee/EventEntryFee";
import { getEventFeesByEventId } from "../eventFee/EventFee";
import { createEventRulesAgreement, getEventRulesAgreementsByEntryId } from "../eventRules/EventRulesAgreement";
import { getEventRulesByEventId } from "../eventRules/EventRules";
import { getEventStallTypesByEventId } from "../eventStallType/EventStallType";
import { createStablingRequest, getStablingRequestsByEventId } from "../stablingRequest/StablingRequest";
import { createEventEntry, getEventEntryById } from "./EventEntry";
import { EventEntryStatusOptions } from "./EventEntryStatus";

interface TargetEventContext {
    eventClasses: EventClass[];
    eventDivisions: EventDivision[];
    eventFees: EventFee[];
    eventStallTypes: EventStallType[];
    eventBeddingTypes: EventBeddingType[];
    eventRules: EventRules[];
}

async function loadTargetEventContext(targetEventId: string): Promise<TargetEventContext> {
    const [classesResult, divisionsResult, feesResult, stallTypesResult, beddingTypesResult, rulesResult] =
        await Promise.all([
            getEventClassesByEventId(targetEventId),
            getEventDivisionsByEventId(targetEventId),
            getEventFeesByEventId(targetEventId),
            getEventStallTypesByEventId(targetEventId),
            getEventBeddingTypesByEventId(targetEventId),
            getEventRulesByEventId(targetEventId),
        ]);

    return {
        eventClasses: classesResult.isSuccess ? classesResult.result ?? [] : [],
        eventDivisions: divisionsResult.isSuccess ? divisionsResult.result ?? [] : [],
        eventFees: feesResult.isSuccess ? feesResult.result ?? [] : [],
        eventStallTypes: stallTypesResult.isSuccess ? stallTypesResult.result ?? [] : [],
        eventBeddingTypes: beddingTypesResult.isSuccess ? beddingTypesResult.result ?? [] : [],
        eventRules: rulesResult.isSuccess ? rulesResult.result ?? [] : [],
    };
}

async function mapDivisionList(
    sourceDivisionList: (string | null)[] | null | undefined,
    isSameEvent: boolean,
    targetContext: TargetEventContext
): Promise<string[]> {
    if (!sourceDivisionList?.length) {
        return [];
    }

    const mappedDivisionIds: string[] = [];

    for (const oldDivisionId of sourceDivisionList) {
        if (!oldDivisionId) continue;

        if (isSameEvent) {
            mappedDivisionIds.push(oldDivisionId);
            continue;
        }

        const oldDivisionResult = await getEventDivisionById(oldDivisionId);
        if (!oldDivisionResult.isSuccess) continue;

        const oldDivision: EventDivision = oldDivisionResult.result;
        const newDivision = targetContext.eventDivisions.find((division) => division.name === oldDivision.name);
        if (newDivision?.id) {
            mappedDivisionIds.push(newDivision.id);
        }
    }

    return mappedDivisionIds;
}

async function copyClassEntries(
    sourceEntry: EventEntry,
    newEntry: EventEntry,
    targetEvent: Event,
    personId: string,
    isSameEvent: boolean,
    targetContext: TargetEventContext
) {
    const classEntriesResult = await getEventClassEntriesWithEventClassByEventEntryId(sourceEntry.id);
    if (!classEntriesResult.isSuccess) return;

    const sourceClassEntries: EventClassEntry[] = classEntriesResult.result ?? [];
    for (const sourceClassEntry of sourceClassEntries) {
        const oldEventClass = sourceClassEntry.eventClass;
        let newEventClassId: string | undefined;

        if (isSameEvent) {
            newEventClassId = oldEventClass?.id;
        } else if (oldEventClass) {
            newEventClassId = targetContext.eventClasses.find((eventClass) => eventClass.number === oldEventClass.number)?.id;
        }

        if (!newEventClassId) continue;

        const classEntryInput: CreateEventClassEntryInput = {
            createdBy: personId,
            lastUpdatedBy: personId,
            eventId: targetEvent.id,
            eventClassId: newEventClassId,
            eventEntryId: newEntry.id,
            riderId: sourceClassEntry.riderId || sourceEntry.riderId || "",
            status: EventEntryStatusOptions.inProgress,
        };
        await createEventClassEntry(classEntryInput);
    }
}

async function copyEntryFees(
    sourceEntry: EventEntry,
    newEntry: EventEntry,
    targetEvent: Event,
    personId: string,
    isSameEvent: boolean,
    targetContext: TargetEventContext
) {
    const feesResult = await getEventEntryFeesByEventIdByEntryId(sourceEntry.eventId, sourceEntry.id);
    if (!feesResult.isSuccess) return;

    const sourceFees: EventEntryFee[] = feesResult.result ?? [];
    for (const sourceFee of sourceFees) {
        let eventFeeId = sourceFee.eventFeeId;
        let feeId = sourceFee.feeId;
        let feeName = sourceFee.name;
        let feeAmount = sourceFee.amount;
        let feeDescription = sourceFee.description;
        let taxA = sourceFee.taxA;
        let taxB = sourceFee.taxB;

        if (!isSameEvent) {
            const matchedEventFee = targetContext.eventFees.find((eventFee) => eventFee.name === sourceFee.name);
            if (!matchedEventFee) continue;

            eventFeeId = matchedEventFee.id;
            feeId = matchedEventFee.feeId;
            feeName = matchedEventFee.name;
            feeAmount = matchedEventFee.amount;
            feeDescription = matchedEventFee.description;
            taxA = matchedEventFee.fee?.taxA ?? null;
            taxB = matchedEventFee.fee?.taxB ?? null;
        }

        const feeInput: CreateEventEntryFeeInput = {
            eventId: targetEvent.id,
            name: feeName,
            status: EventEntryStatusOptions.inProgress,
            amount: feeAmount,
            quantity: sourceFee.quantity || null,
            entryId: newEntry.id,
            eventFeeId: eventFeeId || "",
            feeId: feeId || "",
            taxA,
            taxB,
            description: feeDescription,
            createdBy: personId,
        };
        await createEventEntryFee(feeInput);
    }
}

async function getStablingRequestsForEntry(sourceEntry: EventEntry): Promise<StablingRequest[]> {
    const stablingResult = await getStablingRequestsByEventId(sourceEntry.eventId);
    if (!stablingResult.isSuccess) return [];

    return (stablingResult.result ?? []).filter((request: StablingRequest) =>
        request.entryIds?.includes(sourceEntry.id)
    );
}

async function copyStablingRequests(
    sourceEntry: EventEntry,
    newEntry: EventEntry,
    targetEvent: Event,
    personId: string,
    isSameEvent: boolean,
    targetContext: TargetEventContext
) {
    const sourceStablingRequests = await getStablingRequestsForEntry(sourceEntry);

    for (const sourceStablingRequest of sourceStablingRequests) {
        let stallTypeId = sourceStablingRequest.stallTypeId;
        let pricePerStall = sourceStablingRequest.basePrice;
        let taxA = sourceStablingRequest.taxA;

        if (!isSameEvent) {
            const matchedStallType = targetContext.eventStallTypes.find(
                (stallType) => stallType.name === sourceStablingRequest.stallType?.name
            );
            if (!matchedStallType?.id) continue;

            stallTypeId = matchedStallType.id;
            pricePerStall = matchedStallType.pricePerStall ? parseFloat(matchedStallType.pricePerStall) : null;
            taxA = matchedStallType.taxPerStall ? parseFloat(matchedStallType.taxPerStall) : null;
        }

        const quantityNeeded = sourceStablingRequest.quantityNeeded || null;
        const basePrice = (pricePerStall || 0) * (quantityNeeded || 0);
        const taxRate = (taxA || 0) / 100;
        const fullPrice = basePrice * taxRate + basePrice;

        const stablingInput: CreateStablingRequestInput = {
            eventId: targetEvent.id,
            personId,
            stallTypeId: stallTypeId || "",
            quantityNeeded,
            trainerId: sourceStablingRequest.trainerId || "",
            basePrice: pricePerStall,
            taxA,
            totalPrice: fullPrice,
            status: EventEntryStatusOptions.inProgress,
            requestNote: sourceStablingRequest.requestNote,
            barnId: newEntry.barnId || "",
            entryIds: [newEntry.id],
        };
        await createStablingRequest(stablingInput);
    }
}

async function copyBeddingRequests(
    sourceEntry: EventEntry,
    newEntry: EventEntry,
    targetEvent: Event,
    personId: string,
    isSameEvent: boolean,
    targetContext: TargetEventContext,
    sourceContext?: TargetEventContext
) {
    const beddingResult = await getBeddingRequestsByEntryId(sourceEntry.id);
    if (!beddingResult.isSuccess) return;

    const sourceBeddingRequests: BeddingRequest[] = beddingResult.result ?? [];
    for (const sourceBeddingRequest of sourceBeddingRequests) {
        let beddingTypeId = sourceBeddingRequest.beddingType;
        let pricePerBedding = sourceBeddingRequest.basePrice;
        let taxA = sourceBeddingRequest.beddingRequestTaxAId
            ? parseFloat(sourceBeddingRequest.beddingRequestTaxAId)
            : null;

        if (!isSameEvent && sourceContext) {
            const sourceBeddingType = sourceContext.eventBeddingTypes.find(
                (beddingType) => beddingType.id === sourceBeddingRequest.beddingType
            );
            const matchedBeddingType = targetContext.eventBeddingTypes.find(
                (beddingType) => beddingType.name === sourceBeddingType?.name
            );
            if (!matchedBeddingType?.id) continue;

            beddingTypeId = matchedBeddingType.id;
            pricePerBedding = matchedBeddingType.pricePerBag ? parseFloat(matchedBeddingType.pricePerBag) : null;
            taxA = matchedBeddingType.taxPerBag ? parseFloat(matchedBeddingType.taxPerBag) : null;
        }

        const quantityNeeded = sourceBeddingRequest.quantityNeeded || null;
        const basePrice = (pricePerBedding || 0) * (quantityNeeded || 0);
        const taxRate = (taxA || 0) / 100;
        const fullPrice = basePrice * taxRate + basePrice;

        const beddingInput: CreateBeddingRequestInput = {
            eventId: targetEvent.id,
            personId,
            beddingType: beddingTypeId || "",
            quantityNeeded,
            trainerId: sourceBeddingRequest.trainerId || "",
            basePrice: pricePerBedding,
            totalPrice: fullPrice,
            status: EventEntryStatusOptions.inProgress,
            statusNote: "Copied from a previous entry",
            barnId: newEntry.barnId || "",
            entryIds: [newEntry.id],
            submittedEntry: newEntry.id,
            lastUpdatedBy: personId,
            beddingRequestTaxAId: taxA ? taxA.toString() : "",
        };
        await createBeddingRequest(beddingInput);
    }
}

async function copyRuleAgreements(
    sourceEntry: EventEntry,
    newEntry: EventEntry,
    targetEvent: Event,
    personId: string,
    personType: string | null | undefined,
    isSameEvent: boolean,
    targetContext: TargetEventContext
) {
    const agreementsResult = await getEventRulesAgreementsByEntryId(sourceEntry.id);
    if (!agreementsResult.isSuccess) return;

    const sourceAgreements: EventRulesAgreement[] = agreementsResult.result ?? [];
    for (const sourceAgreement of sourceAgreements) {
        let eventRuleId = sourceAgreement.eventRule?.id;

        if (!isSameEvent) {
            const sourceRuleTitle = sourceAgreement.eventRule?.title;
            const matchedRule = targetContext.eventRules.find((rule) => rule.title === sourceRuleTitle);
            if (!matchedRule?.id) continue;
            eventRuleId = matchedRule.id;
        }

        if (!eventRuleId) continue;

        const agreementInput: CreateEventRulesAgreementInput = {
            eventId: targetEvent.id,
            eventRuleId,
            eventEntryId: newEntry.id,
            name: sourceAgreement.name,
            personId,
            personType: personType || undefined,
            createdBy: personId,
            lastUpdatedBy: personId,
        };
        await createEventRulesAgreement(agreementInput);
    }
}

export async function copyEventEntryToEvent(
    targetEvent: Event,
    sourceEntry: EventEntry,
    personId: string,
    personType?: string | null
): Promise<Result> {
    const sourceResult = await getEventEntryById(sourceEntry.id);
    if (!sourceResult.isSuccess) {
        return {
            isSuccess: false,
            type: "EventEntry",
            result: null,
            message: "Could not load the entry to copy.",
        };
    }

    const source: EventEntry = sourceResult.result;
    const isSameEvent = source.eventId === targetEvent.id;
    const targetContext = await loadTargetEventContext(targetEvent.id);
    const sourceContext = isSameEvent ? targetContext : await loadTargetEventContext(source.eventId);
    const divisionList = await mapDivisionList(source.divisionList, isSameEvent, targetContext);

    const entryInput: CreateEventEntryInput = {
        eventId: targetEvent.id,
        status: EventEntryStatusOptions.inProgress,
        createdBy: personId,
        personId,
        contactId: source.contactId || "",
        riderId: source.riderId,
        riderName: source.riderName,
        horseId: source.horseId,
        horseName: source.horseName,
        ownerId: source.ownerId,
        ownerName: source.ownerName,
        trainerId: source.trainerId,
        trainerName: source.trainerName,
        secondRiderId: source.secondRiderId,
        secondRiderName: source.secondRiderName,
        barnId: source.barnId,
        barnName: source.barnName,
        payerName: source.payerName,
        divisionList,
    };

    const entryResult = await createEventEntry(entryInput);
    if (!entryResult.isSuccess) {
        return entryResult;
    }

    const newEntry: EventEntry = entryResult.result;

    await copyClassEntries(source, newEntry, targetEvent, personId, isSameEvent, targetContext);
    await copyEntryFees(source, newEntry, targetEvent, personId, isSameEvent, targetContext);
    await copyStablingRequests(source, newEntry, targetEvent, personId, isSameEvent, targetContext);
    await copyBeddingRequests(source, newEntry, targetEvent, personId, isSameEvent, targetContext, sourceContext);
    await copyRuleAgreements(source, newEntry, targetEvent, personId, personType, isSameEvent, targetContext);

    return {
        isSuccess: true,
        type: "EventEntry",
        result: newEntry,
        message: "Successfully copied the entry.",
    };
}

export async function copyEventEntriesToEvent(
    targetEvent: Event,
    sourceEntries: EventEntry[],
    personId: string,
    personType?: string | null
): Promise<Result> {
    const copiedEntries: EventEntry[] = [];
    const errors: string[] = [];

    for (const sourceEntry of sourceEntries) {
        const copyResult = await copyEventEntryToEvent(targetEvent, sourceEntry, personId, personType);
        if (copyResult.isSuccess) {
            copiedEntries.push(copyResult.result);
        } else {
            errors.push(copyResult.message || "Could not copy an entry.");
        }
    }

    if (copiedEntries.length === 0) {
        return {
            isSuccess: false,
            type: "EventEntry",
            result: null,
            message: errors.join(" ") || "Could not copy the selected entries.",
        };
    }

    return {
        isSuccess: true,
        type: "EventEntry",
        result: copiedEntries,
        message: `Successfully copied ${copiedEntries.length} entr${copiedEntries.length === 1 ? "y" : "ies"}.`,
    };
}
